import React, { useRef, useState } from "react";
import ReactDOM from "react-dom";
import { useSelector } from "react-redux";
import { Modal, Button, Icon } from "../../components/tailwind";
import {
  GenerationLogDetailLink,
  GenerationLogRows,
  InfoRow,
} from "../../components/GenerationLogRows";
import {
  EnrichGenerationLog,
  getEnrichResultGenerationLog,
} from "../../api/gptApi";
import { RootState } from "../../utils/store";
import { Spinner } from "../../utils/Spinner";

const TEST_IDS = {
  trigger: "enrich-field-row-info-trigger",
  content: "enrich-field-row-info-content",
  usedInstruction: "enrich-field-row-info-used-instruction",
  usedData: "enrich-field-row-info-used-data",
  usedModel: "enrich-field-row-info-used-model",
  usedImages: "enrich-field-row-info-used-images",
  usedWebSearch: "enrich-field-row-info-used-web-search",
  usedReasoning: "enrich-field-row-info-used-reasoning",
  usedApplicability: "enrich-field-row-info-used-applicability",
  tokensUsed: "enrich-field-row-info-tokens-used",
  creditsUsed: "enrich-field-row-info-credits-used",
  detailLink: "enrich-field-row-info-detail-link",
};

type Props = {
  enrichResultId: number;
  disabled: boolean;
};

/** The "i" affordance on an enriched field, opening the log of the run behind it. */
export const EnrichFieldInfo: React.FC<Props> = ({
  enrichResultId,
  disabled,
}) => {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const token = useSelector((state: RootState) => state.auth.token);
  const [log, setLog] = useState<EnrichGenerationLog>();
  const close = (): void => dialogRef.current?.close();

  // Enrich values always come from a Celery task, so the log only ever exists on the
  // saved event and one fetch per field is enough.
  const open = async (): Promise<void> => {
    dialogRef.current?.showModal();
    if (log) {
      return;
    }
    setLog(await getEnrichResultGenerationLog(token, enrichResultId));
  };

  return (
    <>
      <Button
        onClick={open}
        disabled={disabled}
        className="tw-p-1 tw-text-gray-500 hover:!tw-brightness-100 hover:!tw-text-white hover:!tw-shadow-sm !tw-border-gray-500 disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
        title="Generation log"
        size="sm"
      >
        <Icon name="info" className="tw-text-base" />
      </Button>
      {/* Portalled to body: a dialog left inside the table cell picks up an ancestor
          containing block and stops centering on the viewport. */}
      {ReactDOM.createPortal(
        <Modal dialogRef={dialogRef}>
          <Modal.Header divide>
            <h5 className="tw-text-lg tw-font-semibold">Generation log</h5>
          </Modal.Header>
          <Modal.Body className="tw-py-6">
            <div
              data-testid={TEST_IDS.content}
              className="tw-flex tw-flex-col tw-gap-4"
            >
              {log ? (
                <>
                  <GenerationLogDetailLink
                    log={log}
                    testId={TEST_IDS.detailLink}
                  />
                  <InfoRow label="Prompt">
                    {log.prompt_name ?? "Unknown"}
                  </InfoRow>
                  <InfoRow
                    label="Instruction"
                    testId={TEST_IDS.usedInstruction}
                  >
                    {log.instruction ?? "Unknown"}
                  </InfoRow>
                  <GenerationLogRows log={log} testIds={TEST_IDS} />
                  {log.applicability && (
                    <InfoRow
                      label="Applicability gate"
                      testId={TEST_IDS.usedApplicability}
                    >
                      {`Passed at ${Math.round(
                        log.applicability.noul * 100
                      )}% (threshold ${Math.round(
                        log.applicability.threshold * 100
                      )}%)`}
                    </InfoRow>
                  )}
                </>
              ) : (
                <Spinner />
              )}
            </div>
          </Modal.Body>
          <Modal.Actions>
            <Button variant="secondary" type="button" onClick={close}>
              Close
            </Button>
          </Modal.Actions>
        </Modal>,
        document.body
      )}
    </>
  );
};
